import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import Button from 'components/Button';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import SingleCurrencyReview from './SingleCurrencyReview';
import MultiCurrencyReview from './MulticurrencyReview';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import SourceTransactionsContainer from './SourceTransactionsContainer';
import { Alert, CircularProgress, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useDisclosure } from 'further-ui/hooks';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { Row } from 'further-ui/components/Layout';
import { CalendarDay } from 'further-ui/utils';
import { bankReconciliation as rules } from 'further-ui/validations';
import { AllocationRule } from 'further-types/lp/allocations';
import { ActionCommitmentStatus } from 'further-types/lp/action';
import AddBankFeeForm from './AddBankFeeForm';

type TransactionDetails = Transaction & {
  reconcilableTransactionId?: string;
  isRevaluationGainTransaction?: boolean;
  isUnrealisedGainOrLoss?: boolean;
};

type TransactionDetailsWithLcyValues = Omit<
  TransactionDetails,
  'amountExVatLcy' | 'amountIncVatLcy'
> & {
  amountExVatLcy: number;
  amountIncVatLcy: number;
};

type ReconciliationData = {
  transactions: Array<Array<TransactionDetails>>;
  fxRate: CurrencyRateDetails | null;
  unrealisedCashFxGainUnwindTransactions: Array<TransactionDetailsWithLcyValues>;
};

type Props = {
  vehicleId: string;
  bankAccountId: string;
  bankName: string;
  bankOperation: {
    id: string;
    spent?: number;
    received?: number;
    accountCurrency: CurrencyCode;
    date: CalendarDay;
  };
  selectedTransactions: Array<{
    id: string;
    splitFromTransactionId?: string;
    unreconciledAmountTcy: number;
    operationType: ActionOperationType;
    currencyCode: CurrencyCode;
    actionRef: ActionRef;
    ledgerDate: CalendarDay;
  }>;
  onClose: () => void;
};

const defaultBankFeeValues = {
  amountTcy: 0,
  vatRate: 0,
  description: '',
  allocationRule: AllocationRule.ByCommitmentAndClosingDate,
  allocations: [],
  currencyRate: undefined,
  customActionTypeId: null,
  customActionTypeName: null,
  commitmentStatus: ActionCommitmentStatus.InsideCommitment,
  counterparty: null,
};

const ReviewContent: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankName,
  bankOperation,
  selectedTransactions,
  onClose,
}) => {
  const { watch, setValue, getValues, handleSubmit, clearErrors } =
    useFormContext<rules.ReconcileFormValues>();

  const { vehicle } = useVehicle(vehicleId);
  const { hasEqualisationInDraftState } = useEqualisations(vehicleId);
  const { data: generalCapital } = api.capital.getGeneralCapital.useQuery({
    vehicleId,
  });
  const confirmDialog = useDisclosure();

  const [showBankFeeForm, setShowBankFeeForm] = useState(false);

  const bankFeeAmountTcy = watch('bankFee.amountTcy');
  const bankFeeVatRate = watch('bankFee.vatRate');
  const bankFeeCustomActionTypeId = watch('bankFee.customActionTypeId');
  const bankFeeCurrencyRate = watch('bankFee.currencyRate');
  const bankFeeCustomActionTypeName = watch('bankFee.customActionTypeName');

  const bankOperationAmountTcy =
    bankOperation.spent ?? bankOperation.received ?? 0;

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data: reconciliationDetails, isFetching } =
    api.reconciliation.getDetails.useQuery({
      vehicleId,
      bankAccountId,
      bankOperationId: bankOperation.id,
      selectedTransactions: selectedTransactions.map((transaction) => ({
        ...transaction,
        selectedAmount: transaction.unreconciledAmountTcy,
      })),
      bankFeeAmountTcy: bankFeeAmountTcy || undefined,
      bankFeeVatRate: bankFeeVatRate || undefined,
      bankFeeCustomActionTypeId: bankFeeCustomActionTypeId ?? undefined,
      bankFeeCurrencyRate: bankFeeCurrencyRate ?? undefined,
    });

  const [transactions, setTransactions] = useState<
    Array<Array<TransactionDetails>>
  >([]);
  const [
    unrealisedCashFxGainUnwindTransactions,
    setUnrealisedCashFxGainUnwindTransactions,
  ] = useState<Array<TransactionDetailsWithLcyValues>>([]);
  const [bankFeeExpenseTransactions, setBankFeeExpenseTransactions] = useState<
    Array<TransactionDetails>
  >([]);
  const [
    bankFeeReconciliationTransactions,
    setBankFeeReconciliationTransactions,
  ] = useState<Array<TransactionDetails>>([]);

  const fxRate = useMemo(
    () =>
      !reconciliationDetails || 'error' in reconciliationDetails
        ? null
        : reconciliationDetails.currencyRate
        ? reconciliationDetails.currencyRate.source
          ? {
              fetchedRate: reconciliationDetails.currencyRate.rate,
              fetchedSource: reconciliationDetails.currencyRate.source,
            }
          : {
              overrideRate: reconciliationDetails.currencyRate.rate,
              overrideSource: 'Reconciliation',
            }
        : null,
    [reconciliationDetails],
  );

  useEffect(() => {
    if (!reconciliationDetails || 'error' in reconciliationDetails) return;

    const updatedTransactions = [
      ...reconciliationDetails.transactions.map(
        (transaction) => transaction.transactionTypes,
      ),
      reconciliationDetails.foreignCurrencySaleGain.transactions,
    ];
    setTransactions(updatedTransactions);
    setBankFeeExpenseTransactions(
      reconciliationDetails.bankFeeTransactions?.expenseTransactions ?? [],
    );
    setBankFeeReconciliationTransactions(
      reconciliationDetails.bankFeeTransactions?.reconciliationTransactions ??
        [],
    );
  }, [reconciliationDetails]);

  const reconcileOperation =
    api.bankAccount.reconcileBankAccountOperation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Transaction reconciled.',
      }),
    );

  const handleToggleBankFeeForm = (initialAmount?: number) => {
    setShowBankFeeForm((prev) => {
      if (prev) {
        setValue('bankFee', undefined);
        clearErrors('bankFee');
      } else {
        setValue('bankFee', {
          ...defaultBankFeeValues,
          amountTcy: initialAmount ?? 0,
          counterparty: { name: bankName },
          fundingSource: generalCapital?.hasGeneralCapitalCalls
            ? FundingSource.GeneralCapitalOnAccounts
            : FundingSource.CapitalCall,
        });
      }
      return !prev;
    });
  };

  const handleReconcile = () => {
    const bankFeeValues = getValues('bankFee');
    reconcileOperation.mutate({
      vehicleId,
      bankAccountId,
      bankAccountOperationId: bankOperation.id,
      fxRate: fxRate ?? undefined,
      reconciledTransactions: selectedTransactions.map((transaction) => ({
        id: transaction.splitFromTransactionId ?? transaction.id,
        selectedAmount:
          transaction.operationType === ActionOperationType.Spent
            ? -transaction.unreconciledAmountTcy
            : transaction.unreconciledAmountTcy,
        actionRef: transaction.actionRef,
        operationType: transaction.operationType,
        currencyCode: transaction.currencyCode,
      })),
      transactions: transactions.flatMap((transactionsSet) =>
        transactionsSet.map((transaction) => ({
          ...transaction,
          amountIncVatLcy: transaction.amountIncVatLcy ?? 0,
          amountExVatLcy: transaction.amountExVatLcy ?? 0,
          transactionSubType: transaction.transactionSubType,
        })),
      ),
      unrealisedCashFxGainUnwindTransactions,
      bankFee:
        showBankFeeForm && bankFeeValues?.customActionTypeId
          ? {
              amountTcy: bankFeeValues.amountTcy,
              vatRate: bankFeeValues.vatRate,
              description: bankFeeValues.description || undefined,
              allocationRule: bankFeeValues.allocationRule,
              allocations: bankFeeValues.allocations,
              currencyRate: bankFeeValues.currencyRate ?? undefined,
              customActionTypeId: bankFeeValues.customActionTypeId,
              fundingSource: bankFeeValues.fundingSource,
              commitmentStatus: bankFeeValues.commitmentStatus,
              counterparty: bankFeeValues.counterparty,
              expenseTransactions: bankFeeExpenseTransactions.map((t) => ({
                ...t,
                amountIncVatLcy: t.amountIncVatLcy ?? 0,
                amountExVatLcy: t.amountExVatLcy ?? 0,
              })),
              reconciliationTransactions: bankFeeReconciliationTransactions.map(
                (t) => ({
                  ...t,
                  amountIncVatLcy: t.amountIncVatLcy ?? 0,
                  amountExVatLcy: t.amountExVatLcy ?? 0,
                }),
              ),
            }
          : undefined,
    });
  };

  const handleReconcileClick = handleSubmit(
    () => {
      if (hasEqualisationInDraftState) {
        confirmDialog.onOpen();
      } else {
        handleReconcile();
      }
    },
    () => {
      requestAnimationFrame(() => {
        document.querySelector('.Mui-error')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    },
  );

  const isMultiCurrency = selectedTransactions.some(
    (transaction) => transaction.currencyCode !== bankOperation.accountCurrency,
  );

  const handleChangeTransactions = (
    index: number,
    changedTransactions: Array<TransactionDetails>,
  ) => {
    const newTransactions = [...transactions];
    newTransactions[index] = changedTransactions;
    setTransactions(newTransactions);
  };

  return (
    <>
      {showBankFeeForm && (
        <AddBankFeeForm
          vehicleId={vehicleId}
          currency={bankOperation.accountCurrency}
          date={bankOperation.date}
          onRemove={() => handleToggleBankFeeForm()}
        />
      )}
      {isFetching ? (
        <CircularProgress size={24} />
      ) : reconciliationDetails && 'error' in reconciliationDetails ? (
        <Alert severity="error">{reconciliationDetails.error}</Alert>
      ) : (
        reconciliationDetails && (
          <>
            {isMultiCurrency && reconciliationDetails.isValid ? (
              <MultiCurrencyReview
                reconciliationDetails={reconciliationDetails}
                showBankFeeForm={showBankFeeForm}
                onToggleBankFeeForm={handleToggleBankFeeForm}
              />
            ) : (
              <SingleCurrencyReview
                selectedTransactions={selectedTransactions}
                bankOperationAmountTcy={bankOperationAmountTcy}
                currency={bankOperation.accountCurrency}
                bankFeeAmount={bankFeeAmountTcy ?? 0}
                showBankFeeForm={showBankFeeForm}
                onToggleBankFeeForm={handleToggleBankFeeForm}
              />
            )}
            {!!vehicle &&
              !transactionTypesLoading &&
              reconciliationDetails.isValid && (
                <>
                  <Typography variant="h5">
                    2. Review account transactions
                  </Typography>
                  {reconciliationDetails.transactions.map(
                    (transaction, index) => (
                      <React.Fragment
                        key={transaction.reconcilableTransactionId}
                      >
                        <TransactionAllocator
                          title={
                            isAfter(
                              transaction.ledgerDate.endOfDay(),
                              new Date(),
                            )
                              ? 'Account transactions (prepayment)'
                              : 'Account transactions'
                          }
                          transactionTypeOptions={transactionTypeOptions}
                          transactions={transactions[index] ?? []}
                          onChange={(changedTransactions) =>
                            handleChangeTransactions(index, changedTransactions)
                          }
                          transactionCurrency={transaction.transactionsCurrency}
                          vehicle={vehicle}
                          fxRate={fxRate}
                          disableRateChange={true}
                        />
                        {!!transaction.sourceTransactions.length && (
                          <SourceTransactionsContainer
                            showFutureTransactionsMessage={
                              !!transaction.sourceActionHasNoCurrencyRate
                            }
                            bankOperationDate={bankOperation.date}
                            sourceActionDate={transaction.sourceActionDate}
                          >
                            <TransactionAllocator
                              showCurrencyRate
                              transactionTypeOptions={transactionTypeOptions}
                              transactions={transaction.sourceTransactions}
                              onChange={() => {}}
                              transactionCurrency={
                                transaction.transactionsCurrency
                              }
                              vehicle={vehicle}
                              fxRate={
                                transaction.sourceActionCurrencyRate ?? null
                              }
                              readOnly
                            />
                          </SourceTransactionsContainer>
                        )}
                      </React.Fragment>
                    ),
                  )}
                  {!!reconciliationDetails.foreignCurrencySaleGain.transactions
                    .length && (
                    <TransactionAllocator
                      title="Foreign currency sale gain or loss"
                      transactionTypeOptions={transactionTypeOptions}
                      transactions={transactions[transactions.length - 1] ?? []}
                      onChange={(changedTransactions) =>
                        handleChangeTransactions(
                          transactions.length - 1,
                          changedTransactions,
                        )
                      }
                      transactionCurrency={bankOperation.accountCurrency}
                      vehicle={vehicle}
                      fxRate={fxRate}
                      disableRateChange={true}
                      forceShowTcy
                    />
                  )}
                  {!!reconciliationDetails.bankFeeTransactions && (
                    <>
                      <TransactionAllocator
                        title={
                          bankFeeCustomActionTypeName ??
                          'Bank fee - expense transactions'
                        }
                        transactionTypeOptions={transactionTypeOptions}
                        transactions={bankFeeExpenseTransactions}
                        onChange={setBankFeeExpenseTransactions}
                        transactionCurrency={bankOperation.accountCurrency}
                        vehicle={vehicle}
                        fxRate={bankFeeCurrencyRate ?? null}
                        disableRateChange={true}
                      />
                      <TransactionAllocator
                        title={
                          bankFeeCustomActionTypeName
                            ? `${bankFeeCustomActionTypeName} (reconciliation)`
                            : 'Bank fee - reconciliation transactions'
                        }
                        transactionTypeOptions={transactionTypeOptions}
                        transactions={bankFeeReconciliationTransactions}
                        onChange={setBankFeeReconciliationTransactions}
                        transactionCurrency={bankOperation.accountCurrency}
                        vehicle={vehicle}
                        fxRate={bankFeeCurrencyRate ?? null}
                        disableRateChange={true}
                      />
                    </>
                  )}
                  {!!reconciliationDetails.unrealisedCashFxGainUnwind && (
                    <TransactionAllocator
                      title="Unrealised cash fx gain unwind"
                      transactionTypeOptions={transactionTypeOptions}
                      transactions={unrealisedCashFxGainUnwindTransactions}
                      onChange={(transactions) => {
                        setUnrealisedCashFxGainUnwindTransactions(transactions);
                      }}
                      transactionCurrency={bankOperation.accountCurrency}
                      vehicle={vehicle}
                      disableRateChange={true}
                      forceShowTcy
                      fxRate={null}
                    />
                  )}
                  <Row spacing="sm" fullWidth justify="flex-end">
                    <Button variant="outlined" size="large" onClick={onClose}>
                      Cancel and close
                    </Button>
                    <Button
                      size="large"
                      variant="contained"
                      onClick={handleReconcileClick}
                      loading={reconcileOperation.isPending}
                    >
                      Reconcile
                    </Button>
                  </Row>
                  {hasEqualisationInDraftState && (
                    <PendingDraftEqualisationsModal
                      isOpen={confirmDialog.isOpen}
                      onClose={confirmDialog.onClose}
                      onConfirm={handleReconcile}
                      loading={reconcileOperation.isPending}
                    />
                  )}
                </>
              )}
          </>
        )
      )}
    </>
  );
};

export default ReviewContent;
export type { ReconciliationData };
